import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import type { Store } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';

export default class LoanDetailRoute extends Route {
  @service declare sessionContext: SessionContextService;
  @service declare store: Store;

  /**
   * Loads the current person and selected loan account.
   *
   * @param params - Route parameters containing the selected account ID.
   * @returns The data required by both loan-detail variants.
   * @throws When the session has no person ID or the person request fails.
   */
  async model(params: Record<string, unknown>) {
    const personId = this.sessionContext.user.person.id;
    if (!personId) {
      throw new Error('Cannot load loan details without a session person ID.');
    }

    const accountId = params['account_id'] as string;

    const personResult = await this.store.request(
      findRecord<PersonModel>('person', personId, {
        include: 'accounts.financial-institution,accounts.loans,accounts.postal-address',
        reload: true,
      })
    );

    const { content: personContent } = personResult as { content: { data: PersonModel } };
    const person = personContent.data;
    const account = person.activeAccounts?.find(
      (candidate: AccountModel) => candidate.id === accountId
    );

    return { account, person, accountId };
  }
}
